<!-- src/components/admin/AdminDashboard.vue -->

<template>
    <v-app>
      <v-main>
        <v-container>
          <h1 class="pa-14 text-center">Admin Dashboard</h1>
  
          <!-- If not logged in, show login form -->
          <div v-if="!token">
            <v-card max-width="400" class="pa-4 mx-auto">
              <v-card-title class="text-h5">Admin Login</v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="username"
                  label="Username"
                  :rules="[requiredRule]"
                  class="mb-3"
                />
                <v-text-field
                  v-model="password"
                  label="Password"
                  type="password"
                  :rules="[requiredRule]"
                  class="mb-3"
                />
                <v-alert type="error" v-if="errorMsg" class="mt-2">
                  {{ errorMsg }}
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="login" :disabled="!username || !password">
                  Login
                </v-btn>
              </v-card-actions>
            </v-card>
          </div>
  
          <!-- If logged in, show tabs and content -->
          <div v-else>
            <!-- Chalet Price Changer -->
            
            <!-- Tabs Header -->
            <v-tabs
              v-model="currentTab"
              background-color="primary"
              dark
              slider-color="yellow"
              align-with-title
            >
              <v-tab @click="setTab('confirmed')">Confirmed Bookings</v-tab>
              <v-tab @click="setTab('unpaid')">Unpaid Bookings</v-tab>
              <v-tab @click="setTab('chart')">Paid Bookings Chart</v-tab>
              <v-tab @click="setTab('promo')">Promo Codes</v-tab>
              <v-tab @click="setTab('addons')">Add-Ons</v-tab>
              <v-tab @click="setTab('changeprice')">Change Price</v-tab>
            </v-tabs>
  
            <!-- Tabs Content -->
            <div>
              <PriceChanger
              v-if="currentTab === 'changeprice'"
                :token="token"
                @error="handleError"
              />

              <ConfirmedBookings
                v-if="currentTab === 'confirmed'"
                :token="token"
                @error="handleError"
              />
              <UnpaidBookings
                v-if="currentTab === 'unpaid'"
                :token="token"
                @error="handleError"
              />
              <PaidBookingsChart
                v-if="currentTab === 'chart'"
                :token="token"
                @error="handleError"
              />
              <PromoCodes
                v-if="currentTab === 'promo'"
                :token="token"
                @error="handleError"
              />
              <AddOns
                v-if="currentTab === 'addons'"
                :token="token"
                @error="handleError"
              />
            </div>
  
            <!-- =================== Download & Logout =================== -->
            <div class="mt-4 text-right">
              <v-btn color="blue darken-1" class="mr-2" @click="downloadExcel">
                Download Excel
              </v-btn>
              <v-btn color="red darken-2" @click="logout">
                Logout
              </v-btn>
            </div>
  
            <!-- =================== Global Error Alert =================== -->
            <v-alert type="error" v-if="errorMsg" class="mt-2">
              {{ errorMsg }}
            </v-alert>
          </div>
        </v-container>
      </v-main>
    </v-app>
  </template>
  
  <script>
  import apiService from '@/services/apiService.js';
  import ConfirmedBookings from './ConfirmedBookings.vue';
  import UnpaidBookings from './UnpaidBookings.vue';
  import PaidBookingsChart from './PaidBookingsChart.vue';
  import PromoCodes from './PromoCodes.vue';
  import AddOns from './AddOns.vue';
  import PriceChanger from './PriceChanger.vue';
  
  export default {
    name: "AdminDashboard",
    components: {
      ConfirmedBookings,
      UnpaidBookings,
      PaidBookingsChart,
      PromoCodes,
      AddOns,
      PriceChanger
    },
    data() {
      return {
        username: "",
        password: "",
        token: null,
        errorMsg: null,
        currentTab: 'confirmed', // Default active tab
      };
    },
    computed: {
      requiredRule() {
        return v => !!v || "Required field";
      },
    },
    methods: {
      login() {
        this.errorMsg = null;
        apiService.loginAdmin({ username: this.username, password: this.password })
          .then(res => {
            this.token = res.data.token;
            this.currentTab = 'confirmed'; // Set to first tab after login
          })
          .catch(err => {
            this.errorMsg = err.response?.data?.error || "Login failed.";
          });
      },
      logout() {
        this.token = null;
        this.username = "";
        this.password = "";
        this.errorMsg = null;
        this.currentTab = 'confirmed';
      },
      setTab(tabName) {
        this.currentTab = tabName;
      },
      downloadExcel() {
        // Fetch all bookings to download
        Promise.all([
          apiService.fetchPaidBookings(this.token),
          apiService.fetchUnpaidBookings(this.token)
        ])
        .then(([paidRes, unpaidRes]) => {
          const all = [...paidRes.data, ...unpaidRes.data];
          if (!all.length) {
            alert("No bookings to download!");
            return;
          }
          let csvContent = "id,name,phone,start_date,end_date,total_price,numofdays,payment_status,note\n";
          for (const b of all) {
            const row = [
              b.id,
              `"${b.name.replace(/"/g, '""')}"`, // Escape quotes
              `"${b.phone.replace(/"/g, '""')}"`,
              b.start_date,
              b.end_date,
              b.total_price,
              b.numofdays,
              b.payment_status,
              `"${(b.note || '').replace(/"/g, '""')}"`
            ].join(",");
            csvContent += row + "\n";
          }
          const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", "bookings.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          alert("Failed to download bookings.");
        });
      },
      handleError(message) {
        this.errorMsg = message;
      }
    }
  };
  </script>
  
  <style scoped>
  .text-right {
    text-align: right;
  }
  .mt-4 {
    margin-top: 16px;
  }
  .mr-2 {
    margin-right: 8px;
  }
  .mx-auto {
    margin-left: auto;
    margin-right: auto;
  }
  </style>
  